import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

export interface IUseState {
    locks: Set<any>;
    obs: BehaviorSubject<boolean>;
}

/**
 * Utility service for marking that something needs some state.
 * For example multiple element may request setting up a lock and things remains locked till all of them are unlocked.
 *
 * onDragStart () => release = this.clickLockService.lock(this)
 * onDragStop () => release()
 */
/**
 * Service for managing locks and states.
 */
@Injectable({
    providedIn: 'root'
})
export class UseService {
    /**
     * Use state index
     * each entry is list of items that do require that entry
     */
    private states: { [key: string]: IUseState } = {};

    /**
     * Lock entry
     * @param key - The key of the entry to lock.
     * @param source - The source of the lock.
     */
    lock(key: string, source: any) {
        const locks = this.getState(key).locks;
        if (!locks.has(source)) {
            locks.add(source);
            this.emit(key);
        }
    }

    /**
     * Unlock entry
     * @param key - The key of the entry to unlock.
     * @param source - The source of the unlock.
     */
    unlock(key: string, source: any) {
        const locks = this.getState(key).locks;
        if (locks.has(source)) {
            locks.delete(source);
            this.emit(key);
        }
    }

    /**
     * Reset entry (drop all locks)
     * @param key - The key of the entry to reset.
     */
    reset(key: string) {
        this.getState(key).locks.clear();
        this.emit(key);
    }

    /**
     * Get the state of an entry.
     * @param key - The key of the entry.
     * @returns The state of the entry.
     */
    getState(key: string): IUseState {
        const state = this.states;
        if (!state[key]) {
            state[key] = { locks: new Set(), obs: new BehaviorSubject<boolean>(false) };
        }
        return state[key];
    }

    /**
     * Check if an entry is locked.
     * @param key - The key of the entry.
     * @returns A boolean indicating if the entry is locked.
     */
    isLocked(key: string): boolean {
        return this.getState(key).locks.size > 0;
    }

    /**
     * Get the observable for an entry.
     * @param key - The key of the entry.
     * @returns The observable for the entry.
     */
    get(key: string): Subject<boolean> {
        return this.getState(key).obs;
    }

    /**
     * Subscribe to changes in the lock state of an entry.
     * @param key - The key of the entry.
     * @param cb - The callback function to be called when the lock state changes.
     * @returns A subscription object that can be used to unsubscribe from the changes.
     */
    subscribe(key: string, cb: (locked: boolean) => void): Subscription {
        return this.getState(key).obs.subscribe(cb);
    }

    private emit(key: string) {
        const obs = this.getState(key).obs;
        const isLocked = this.isLocked(key);
        if (obs.value !== isLocked) {
            obs.next(isLocked);
        }
    }
}
